/**
 * Lift2Fit — admin: one account (`#/admin/accounts/:userId`).
 *
 * The account's AGGREGATE detail plus the deliberate, audited writes. The admin
 * sees counts, plans, seats, dates and money; never a set, a session body or a
 * business's client roster. Every change goes through a confirmation that
 * states exactly what will be written, and the audit entry the API returns is
 * shown on screen.
 */
import { useState } from 'react';
import {
  api,
  type AdminAccountPatch,
  type AdminAuditEntry,
  type CoachPlan,
  type Problem,
} from '@/lib/api';
import { formatCompactCount, formatCompactMoney, formatCount, formatDate } from '@/lib/format';
import { AlertIcon, ArrowRightIcon, ShieldIcon } from '@/lib/icons';
import { Button } from '@/components/ui';
import {
  AppLinkButton,
  EmptyState,
  LoadingPanel,
  Panel,
  ScreenHeader,
  StatusChip,
  type ChipTone,
} from '../components';
import { formatMoney } from '../crm/shared';
import { useAsync } from '../hooks';
import { Link } from '../router';
import {
  ADMIN_EYEBROW,
  AdminRow,
  AdminStat,
  ChangeConfirm,
  coachPlanLabel,
  coachPlanSize,
  roleLabel,
  seatLimitLabel,
  tierLabel,
} from './shared';

const PLAN_OPTIONS: ReadonlyArray<CoachPlan> = ['free', 'coach', 'gym', 'gym_plus', 'scale'];

interface PendingChange {
  summary: string;
  patch: AdminAccountPatch;
}

interface Notice {
  text: string;
  tone: ChipTone;
}

/** `YYYY-MM-DDTHH:mm` in local time for a `datetime-local` input. */
function toDateTimeInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function AdminAccountScreen({ userId }: { userId: string }) {
  const state = useAsync(() => api.adminAccount(userId), [userId]);
  const account = state.data;

  const [plan, setPlan] = useState<CoachPlan | ''>('');
  const [tier, setTier] = useState<'basic' | 'premium' | ''>('');
  const [premiumUntil, setPremiumUntil] = useState('');
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [lastAudit, setLastAudit] = useState<AdminAuditEntry | null>(null);

  const issue = (problem: Problem | undefined, fallbackText: string) => {
    setNotice(null);
    setRefusal(problem?.detail ?? problem?.title ?? fallbackText);
  };

  const apply = async (change: PendingChange) => {
    setBusy(true);
    setRefusal(null);
    setNotice(null);
    const result = await api.adminPatchAccount(userId, change.patch);
    setBusy(false);
    if (result.problem || !result.data) {
      setPending(null);
      issue(result.problem, 'That change was refused.');
      return;
    }
    setPending(null);
    setLastAudit(result.data.audit);
    state.reload();
    setNotice({
      text: result.data.audit
        ? `Written and audited: ${result.data.audit.summary}.`
        : 'Nothing changed — the account already held those values.',
      tone: result.data.audit ? 'success' : 'info',
    });
  };

  if (state.loading && !account) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingPanel label="Loading the account" rows={4} />
        <LoadingPanel label="Loading activity and money" rows={3} />
      </div>
    );
  }

  if (!account) {
    return (
      <EmptyState
        title="We could not read that account"
        body={state.error ?? 'The admin API did not answer, or the account does not exist.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/admin/accounts" variant="outline">
            Back to accounts
          </AppLinkButton>
        }
      />
    );
  }

  const isBusiness = account.seats !== null;

  return (
    <div className="flex flex-col gap-6" data-testid="admin-account-detail">
      <ScreenHeader
        eyebrow={`${ADMIN_EYEBROW} · account`}
        title={account.displayName}
        badge={<StatusChip tone="danger">Founder only</StatusChip>}
        lead={`${account.email} · created ${formatDate(account.createdAt)} · last activity ${
          account.lastActivityAt ? formatDate(account.lastActivityAt) : 'never'
        }`}
        actions={
          <AppLinkButton to="/admin/accounts" variant="outline">
            All accounts
          </AppLinkButton>
        }
      />

      {refusal ? (
        <p role="alert" data-testid="admin-account-refusal" className="border-l-2 border-danger bg-page-subtle px-3 py-2 text-[13px] text-danger">
          {refusal}
        </p>
      ) : null}
      <p
        aria-live="polite"
        data-testid="admin-account-notice"
        className={`min-h-[20px] text-[13px] ${notice?.tone === 'success' ? 'text-success' : 'text-ink-muted'}`}
      >
        {notice?.text ?? ''}
      </p>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Identity & plan" testId="admin-account-identity">
          <div className="flex flex-col">
            <AdminRow
              label="Roles"
              value={
                <span className="flex flex-wrap justify-end gap-1">
                  {account.roles.map((entry) => (
                    <StatusChip key={entry} tone={entry === 'admin' ? 'danger' : entry === 'coach' ? 'accent' : 'neutral'}>
                      {roleLabel(entry)}
                    </StatusChip>
                  ))}
                </span>
              }
            />
            <AdminRow label="Tier" value={tierLabel(account.tier)} />
            <AdminRow label="Premium until" value={account.premiumUntil ? formatDate(account.premiumUntil) : '—'} />
            <AdminRow label="Coach plan" value={`${coachPlanLabel(account.coachPlan)} · ${coachPlanSize(account.coachPlan)}`} />
            <AdminRow
              label="Seats"
              value={
                account.seats === null
                  ? 'not a business'
                  : `${formatCount(account.seats.used)} / ${seatLimitLabel(account.seats.limit)}`
              }
            />
            {account.seats ? (
              <AdminRow
                label="Seats detail"
                value={`${formatCount(account.seats.active)} active · ${formatCount(account.seats.pending)} pending`}
              />
            ) : null}
          </div>
        </Panel>

        <Panel title="Usage (aggregates only)" testId="admin-account-usage">
          <div className="grid grid-cols-2 gap-3">
            <AdminStat
              label="Sessions completed"
              value={formatCompactCount(account.sessionsCompleted)}
              valueTitle={formatCount(account.sessionsCompleted)}
              hint={`${formatCount(account.sessionsLast28)} in the last 28 days`}
            />
            <AdminStat
              label="Sets logged"
              value={formatCompactCount(account.setsLogged)}
              valueTitle={formatCount(account.setsLogged)}
              hint="count only"
            />
            {isBusiness ? (
              <>
                <AdminStat label="Active clients" value={formatCompactCount(account.clients ?? 0)} valueTitle={formatCount(account.clients ?? 0)} />
                <AdminStat label="Open slots" value={formatCompactCount(account.openSlots ?? 0)} valueTitle={formatCount(account.openSlots ?? 0)} />
                <AdminStat label="Bookings" value={formatCompactCount(account.bookings ?? 0)} valueTitle={formatCount(account.bookings ?? 0)} />
                <AdminStat
                  label="Paid / due"
                  value={
                    account.revenue
                      ? `${formatCompactMoney(account.revenue.paid, account.revenue.currency)} / ${formatCompactMoney(account.revenue.due, account.revenue.currency)}`
                      : '—'
                  }
                  valueTitle={
                    account.revenue
                      ? `${formatMoney(account.revenue.paid, account.revenue.currency)} / ${formatMoney(account.revenue.due, account.revenue.currency)}`
                      : undefined
                  }
                  hint={account.revenue ? `${formatCompactMoney(account.revenue.refunded, account.revenue.currency)} refunded` : undefined}
                />
              </>
            ) : (
              <AdminStat label="Business" value="—" hint="this account is not a coaching business" />
            )}
          </div>
          <p className="text-[12px] text-ink-muted">
            The admin reads counts, plans, seats and money. It does not read this account's sets, session bodies or client roster.
          </p>
        </Panel>
      </div>

      <Panel
        title="Deliberate changes"
        subtitle="Every write is confirmed first and recorded in the audit trail with its before/after."
        testId="admin-account-changes"
      >
        {pending ? (
          <ChangeConfirm
            summary={pending.summary}
            busy={busy}
            onConfirm={() => void apply(pending)}
            onCancel={() => setPending(null)}
          />
        ) : null}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="flex flex-col gap-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Coaching plan</span>
            <div className="flex flex-wrap items-end gap-2">
              <select
                data-testid="admin-plan-select"
                value={plan === '' ? account.coachPlan : plan}
                onChange={(event) => setPlan(event.target.value as CoachPlan)}
                className="h-10 border border-hairline bg-page px-2 text-[13px] text-ink"
              >
                {PLAN_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {coachPlanLabel(option)} — {coachPlanSize(option)}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                variant="outline"
                data-testid="admin-plan-review"
                onClick={() =>
                  setPending({
                    summary: `coachPlan ${account.coachPlan} → ${plan === '' ? account.coachPlan : plan}`,
                    patch: { coachPlan: plan === '' ? account.coachPlan : plan },
                  })
                }
              >
                Review change
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Lifter tier</span>
            <div className="flex flex-wrap items-end gap-2">
              <select
                data-testid="admin-tier-select"
                value={tier === '' ? (account.tier === 'premium' ? 'premium' : 'basic') : tier}
                onChange={(event) => setTier(event.target.value as 'basic' | 'premium')}
                className="h-10 border border-hairline bg-page px-2 text-[13px] text-ink"
              >
                <option value="basic">Free</option>
                <option value="premium">Premium</option>
              </select>
              <input
                type="datetime-local"
                data-testid="admin-premium-until"
                value={premiumUntil}
                onChange={(event) => setPremiumUntil(event.target.value)}
                className="h-10 border border-hairline bg-page px-2 text-[12px] text-ink"
                aria-label="Premium until (optional)"
              />
              <Button
                size="sm"
                variant="outline"
                data-testid="admin-tier-review"
                onClick={() => {
                  const nextTier =
                    tier === '' ? (account.tier === 'premium' ? 'premium' : 'basic') : tier;
                  const nextUntil = premiumUntil === '' ? undefined : new Date(premiumUntil).toISOString();
                  setPending({
                    summary: `tier ${account.tier} → ${nextTier}${nextUntil ? `, premiumUntil ${nextUntil.slice(0, 10)}` : ''}`,
                    patch: { tier: nextTier, ...(nextUntil ? { premiumUntil: nextUntil } : {}) },
                  });
                }}
              >
                Review change
              </Button>
              <Button
                size="sm"
                variant="ghost"
                data-testid="admin-premium-clear"
                onClick={() =>
                  setPending({ summary: 'premiumUntil → none', patch: { premiumUntil: null } })
                }
              >
                Clear premium window
              </Button>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-hairline pt-3">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Roles</span>
          {account.roles.includes('coach') ? (
            <Button
              size="sm"
              variant="ghost"
              data-testid="admin-revoke-coach"
              onClick={() => setPending({ summary: 'role -coach', patch: { revokeRole: 'coach' } })}
            >
              Revoke coach
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              data-testid="admin-grant-coach"
              onClick={() => setPending({ summary: 'role +coach', patch: { grantRole: 'coach' } })}
            >
              Grant coach
            </Button>
          )}
          {account.roles.includes('admin') ? (
            <Button
              size="sm"
              variant="ghost"
              data-testid="admin-revoke-admin"
              onClick={() => setPending({ summary: 'role -admin', patch: { revokeRole: 'admin' } })}
            >
              Revoke admin
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              data-testid="admin-grant-admin"
              onClick={() => setPending({ summary: 'role +admin', patch: { grantRole: 'admin' } })}
            >
              Grant admin
            </Button>
          )}
        </div>

        <div
          data-testid="admin-account-audit"
          className="flex flex-col gap-1 border-t border-hairline pt-3 text-[13px]"
        >
          {lastAudit ? (
            <>
              <span className="font-bold text-ink">Audit entry written</span>
              <span className="text-ink-muted">
                {lastAudit.summary} · {lastAudit.actorEmail ?? 'admin'} · {formatDate(lastAudit.createdAt)}
              </span>
            </>
          ) : (
            <span className="text-ink-muted">No change made in this session yet.</span>
          )}
        </div>
      </Panel>

      <p className="text-[12px] text-ink-muted">
        <Link to="/admin/audit" className="inline-flex items-center gap-1 text-accent hover:text-accent-hover">
          <ShieldIcon className="text-[12px]" />
          Open the full audit trail
          <ArrowRightIcon />
        </Link>
      </p>
    </div>
  );
}
